import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Info, AlertCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { publications } from '../data/publications';
import { timelineEvents as importedTimelineEvents } from '../data/timeline';

// Location coordinates mapping for Nigerian cities
const locationCoordinates: { [key: string]: [number, number] } = {
  'Ibadan': [7.3776, 3.9470],
  'Ile-Ife': [7.4667, 4.5667],
  'Oyo-Ile': [8.8370, 4.2530],
  'Oyo': [7.8500, 3.9333],
  'Oyo Empire': [7.8500, 3.9333],
  'Abeokuta': [7.1475, 3.3619],
  'Ijaye': [7.5833, 3.7500],
  'Lagos': [6.4541, 3.3947],
  'Eko': [6.4541, 3.3947],
  'Lagos Island': [6.4541, 3.3947],
  'Owu': [7.1600, 3.3500],
  'Ogbomoso': [8.1333, 4.2500],
  'Osogbo': [7.7667, 4.5667],
  'Ikirun': [7.9000, 4.6667],
  'Ilorin': [8.5000, 4.5500],
  'Benin Kingdom': [6.3350, 5.6270],
  'Benin City': [6.3350, 5.6270],
  'Ilesha': [7.6167, 4.6833],
  'Ijesha': [7.6167, 4.6833],
  'Igboho': [8.8333, 3.7500],
  'Eba-Odan': [7.3750, 3.9500],
  'Eba Odan': [7.3750, 3.9500],
  'Igbo Ipara': [7.3750, 3.9500],
  'Igbo-Ipara': [7.3750, 3.9500],
  'Mapo Hall': [7.3834, 3.9382],
  'Iseri': [6.5, 3.2],
  'Iddo': [6.4541, 3.3947],
  'Gbere': [8.8370, 4.2530],
  'Ibariba country': [9.5, 3.0],
  'Yoruba Country': [7.8500, 3.9333],
  'Erin-Oke': [7.7, 4.6],
  'Erin-Ijesa': [7.7, 4.6],
  'Meroe': [85, 25],
  'Sudan': [85, 25]
};

// Helper function to create custom marker icon
const createMarkerIcon = (color: string) => new L.DivIcon({
  html: `<svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z"
      fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
  </svg>`,
  className: '',
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36]
});

// Helper function to get category color
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'founding':
    case 'cultural':
      return '#D4AF37'; // gold
    case 'governance':
      return '#064635'; // dark green
    case 'war':
      return '#dc2626'; // red
    case 'colonial':
    case 'modern':
      return '#2563eb'; // blue
    case 'landmark':
      return '#059669'; // teal
    default:
      return '#D4AF37';
  }
};

// Helper function to offset markers at same location
const offsetMarker = (lat: number, lng: number, index: number, total: number): [number, number] => {
  if (total <= 1) return [lat, lng];
  const angle = (2 * Math.PI * index) / total;
  const radius = 0.003;
  return [lat + radius * Math.cos(angle), lng + radius * Math.sin(angle)];
};

// Combine all locations from publications and convert to lat/lng
interface HistoricalLandmark {
  id: string;
  name: string;
  coordinates: [number, number];
  description: string;
  publicationId: string;
  publicationTitle: string;
  type: 'location';
}

const historicalLandmarks = publications.reduce((acc, pub) => {
  if (pub.locations) {
    const landmarks = pub.locations
      .map(loc => {
        const coords = locationCoordinates[loc.name];
        if (!coords) return null;

        return {
          id: `${pub.id}-${loc.name}`,
          name: loc.name,
          coordinates: coords as [number, number],
          description: loc.description,
          publicationId: pub.id,
          publicationTitle: pub.title,
          type: 'location' as const
        };
      })
      .filter((loc): loc is HistoricalLandmark => loc !== null);
    return [...acc, ...landmarks];
  }
  return acc;
}, [] as HistoricalLandmark[]);

// Map timeline events to lat/lng coordinates based on their primary location
interface TimelineMapMarker {
  id: string;
  name: string;
  coordinates: [number, number];
  description: string;
  category: string;
  locations: string[];
  keyFigures: string[];
  year: number;
  yearRange?: string;
  type: 'event';
}

const timelineMapMarkers = importedTimelineEvents
  .filter(event => event.locations && event.locations.length > 0)
  .map(event => {
    const primaryLocation = event.locations[0];
    const coords = locationCoordinates[primaryLocation];
    if (!coords) return null;

    return {
      id: event.id,
      name: `${event.title} (${event.year})`,
      coordinates: coords as [number, number],
      description: event.description,
      category: event.category,
      locations: event.locations,
      keyFigures: event.keyFigures,
      year: event.year,
      yearRange: event.yearRange,
      type: 'event' as const
    };
  })
  .filter((marker): marker is TimelineMapMarker => marker !== null);

// Use imported timeline events for the timeline display
const timelineEvents = [...importedTimelineEvents].sort((a, b) => a.year - b.year);

export default function InteractiveFeatures() {
  const [selectedLandmark, setSelectedLandmark] = useState<HistoricalLandmark | TimelineMapMarker | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [showLandmarkDetails, setShowLandmarkDetails] = useState(false);
  const [mapError, setMapError] = useState(false);
  const location = useLocation();

  const toggleEventExpansion = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const findPublicationByTitle = (title: string) => {
    return publications.find(pub =>
      pub.title.toLowerCase().includes(title.toLowerCase()) ||
      title.toLowerCase().includes(pub.title.toLowerCase())
    );
  };

  const createPublicationSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const allMapMarkers = [...historicalLandmarks, ...timelineMapMarkers];
  const totalMapMarkers = allMapMarkers.length;

  // Group markers by location and apply offsets
  const groupedMarkers = allMapMarkers.reduce((acc, marker) => {
    const key = `${marker.coordinates[0]},${marker.coordinates[1]}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(marker);
    return acc;
  }, {} as Record<string, (HistoricalLandmark | TimelineMapMarker)[]>);

  const markersWithOffsets = Object.values(groupedMarkers).flatMap((markers) => {
    return markers.map((marker, index) => ({
      ...marker,
      offsetCoordinates: offsetMarker(marker.coordinates[0], marker.coordinates[1], index, markers.length)
    }));
  });

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Historical Map Section */}
        <section id="map" className="mb-20">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-[#064635] mb-4">Interactive Historical Map</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              Explore the historical landmarks of Ibadan and surrounding regions through our interactive map.
              Click on the markers to learn more about each location's significance in Yoruba history.
            </p>

            {/* Statistics and Notice */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-4 border border-[#D4AF37]/20">
                <div className="flex items-center justify-center gap-6 text-sm mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-[#064635] font-medium">
                      {totalMapMarkers} Map Markers
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#D4AF37]" />
                    <span className="text-[#064635] font-medium">
                      {timelineEvents.length} Historical Events
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-600">
                  <Info className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-left">
                    Map shows historical events and landmarks from Ibadan and Yorubaland spanning from ancient times to the 18th century.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {allMapMarkers.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <MapPin className="h-16 w-16 text-[#D4AF37] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-[#064635] mb-2">
                Map Locations Coming Soon
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Historical locations and landmarks are being added as publications are digitized.
                Check back soon to explore the geography of Ibadan's history.
              </p>
            </div>
          ) : (
            <div className="relative h-[500px] md:h-[500px] sm:h-[350px] bg-[#F5F5DC] rounded-xl shadow-lg overflow-hidden border-4 border-[#D4AF37]/20">
              <MapContainer
                center={[7.38, 3.94]}
                zoom={8}
                scrollWheelZoom={true}
                className="h-full w-full z-0"
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {markersWithOffsets.map((marker) => {
                  const color = marker.type === 'event'
                    ? getCategoryColor(marker.category)
                    : '#D4AF37';

                  return (
                    <Marker
                      key={marker.id}
                      position={marker.offsetCoordinates}
                      icon={createMarkerIcon(color)}
                    >
                      <Popup className="custom-popup" maxWidth={300}>
                        <div className="p-2">
                          <div className="flex items-center gap-2 mb-2">
                            {marker.type === 'event' ? (
                              <Clock className="h-5 w-5" style={{ color }} />
                            ) : (
                              <MapPin className="h-5 w-5" style={{ color }} />
                            )}
                            <h3 className="font-bold text-[#064635] text-sm">{marker.name}</h3>
                          </div>

                          <p className="text-gray-600 text-xs mb-3">{marker.description}</p>

                          {marker.type === 'event' && (
                            <>
                              <div className="mb-2">
                                <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-[#D4AF37]/20 text-[#064635] capitalize">
                                  {marker.category}
                                </span>
                              </div>

                              {marker.locations && marker.locations.length > 0 && (
                                <div className="mb-2 text-xs">
                                  <p className="font-semibold text-[#064635]">Locations:</p>
                                  <p className="text-gray-600">{marker.locations.join(', ')}</p>
                                </div>
                              )}

                              {marker.keyFigures && marker.keyFigures.length > 0 && (
                                <div className="mb-2 text-xs">
                                  <p className="font-semibold text-[#064635]">Key Figures:</p>
                                  <p className="text-gray-600">{marker.keyFigures.join(', ')}</p>
                                </div>
                              )}

                              {marker.yearRange && (
                                <div className="text-xs">
                                  <p className="font-semibold text-[#064635]">Time Period:</p>
                                  <p className="text-gray-600">{marker.yearRange}</p>
                                </div>
                              )}
                            </>
                          )}

                          {marker.type === 'location' && 'publicationId' in marker && marker.publicationId && (
                            <div className="text-xs">
                              <p className="font-semibold text-[#064635] mb-1">Referenced in:</p>
                              <a
                                href={`/publications/${marker.publicationId}`}
                                className="text-[#D4AF37] hover:underline"
                              >
                                {'publicationTitle' in marker ? marker.publicationTitle : ''}
                              </a>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>

              {/* Map Legend */}
              <div className="absolute left-4 bottom-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg
                            border border-[#D4AF37]/20 z-[1000]">
                <h4 className="text-sm font-semibold text-[#064635] mb-3">Map Legend</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-[#D4AF37]"></div>
                    <span className="text-xs text-gray-600">Founding/Cultural</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-[#064635]"></div>
                    <span className="text-xs text-gray-600">Governance</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-[#dc2626]"></div>
                    <span className="text-xs text-gray-600">War</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-[#2563eb]"></div>
                    <span className="text-xs text-gray-600">Colonial</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Timeline Section */}
        <section id="timeline">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#064635] mb-4">Timeline of Ibadan</h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-6">
              Journey through centuries of Ibadan's history, from its founding as a war camp to its evolution
              as a major metropolitan hub.
            </p>

            {/* Timeline Statistics */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-4 border border-[#D4AF37]/20">
                <div className="flex items-center justify-center gap-2 text-sm mb-3">
                  <Clock className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-[#064635] font-medium">
                    {timelineEvents.length} Historical Events
                  </span>
                </div>
                <div className="flex items-start gap-2 text-xs text-gray-600">
                  <Info className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-left">
                    Additional timeline events are being catalogued and will be added as more publications
                    are digitized and cross-referenced.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {timelineEvents.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <Clock className="h-16 w-16 text-[#D4AF37] mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-[#064635] mb-2">
                Timeline Coming Soon
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Historical timeline events are being catalogued as publications are digitized.
                Check back soon to explore the chronological history of Ibadan.
              </p>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 bg-[#D4AF37]/30" />
              <div className="space-y-6">
                {timelineEvents.map((event, index) => {
                  const isExpanded = expandedEvents.has(event.id);
                  const isLeft = index % 2 === 0;

                  return (
                    <div
                      key={event.id}
                      className={`relative ${
                        isLeft
                          ? 'md:pr-[calc(50%+1.5rem)] pl-10 md:pl-0'
                          : 'md:pl-[calc(50%+1.5rem)] pl-10'
                      }`}
                    >
                      {/* Timeline dot */}
                      <div className="absolute left-4 md:left-1/2 top-4 transform md:-translate-x-1/2 w-3 h-3 rounded-full bg-[#D4AF37] border-2 border-white shadow-md z-10" />

                      <div className={`flex ${isLeft ? 'md:justify-end' : 'md:justify-start'}`}>
                        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg w-full md:max-w-xl">
                          {/* Collapsed View */}
                          <div className="p-4">
                            <div className="flex items-start gap-3 mb-2">
                              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#D4AF37] flex items-center justify-center">
                                <span className="text-[#064635] font-bold text-xs text-center px-1">
                                  {event.yearRange || event.year}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-bold text-[#064635] mb-1 line-clamp-2">
                                  {event.title}
                                </h3>
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#D4AF37]/20 text-[#064635] capitalize">
                                  {event.category}
                                </span>
                              </div>
                            </div>

                            {event.locations && event.locations.length > 0 && (
                              <div className="text-xs text-gray-600 mb-2 line-clamp-1">
                                <span className="font-semibold text-[#064635]">Locations:</span> {event.locations.join(', ')}
                              </div>
                            )}

                            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                              <div className="flex gap-3">
                                {event.keyFigures && event.keyFigures.length > 0 && (
                                  <span>Key Figures: {event.keyFigures.length}</span>
                                )}
                                {event.sourcePublications && event.sourcePublications.length > 0 && (
                                  <span>Sources: {event.sourcePublications.length}</span>
                                )}
                              </div>
                            </div>

                            {/* Expand/Collapse Button */}
                            <button
                              onClick={() => toggleEventExpansion(event.id)}
                              className="flex items-center gap-1 text-xs font-medium text-[#D4AF37] hover:text-[#064635] transition-colors"
                            >
                              {isExpanded ? 'Hide details' : 'View details'}
                              <svg
                                className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>

                          {/* Expanded View */}
                          <div
                            className={`transition-all duration-300 ease-in-out ${
                              isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                            } overflow-hidden`}
                          >
                            <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                                {event.description}
                              </p>

                              {event.keyFigures && event.keyFigures.length > 0 && (
                                <div className="mb-3 text-sm">
                                  <p className="font-semibold text-[#064635] mb-1">Key Figures:</p>
                                  <p className="text-gray-600">{event.keyFigures.join(', ')}</p>
                                </div>
                              )}

                              {event.sourcePublications && event.sourcePublications.length > 0 && (
                                <div className="text-sm">
                                  <p className="font-semibold text-[#064635] mb-1">Source Publications:</p>
                                  <ul className="space-y-1">
                                    {event.sourcePublications.map((source, idx) => {
                                      const matchedPub = findPublicationByTitle(source);
                                      if (matchedPub) {
                                        return (
                                          <li key={idx}>
                                            <a
                                              href={`/publications/${matchedPub.id}`}
                                              className="text-[#D4AF37] hover:text-[#064635] hover:underline text-xs transition-colors"
                                            >
                                              {source}
                                            </a>
                                          </li>
                                        );
                                      }
                                      return (
                                        <li key={idx} className="text-gray-600 text-xs">
                                          {source}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}